import React from 'react';
import 'whatwg-fetch';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import extend from 'lodash/extend';
import CapNotification from '@capillarytech/cap-ui-library/CapNotification';
import decompressJsonObject from '@capillarytech/cap-ui-utils/utils/zlibDataDecompress';
import { removeAuthenticationDetais } from '../utils/authWrapper';
import config from '../config/app';
import * as requestConstructor from './requestConstructor';
import { loadItem } from './localStorageApi';
import { getQueryParams } from '../components/pages/Cap/utils';
import * as AxiosApi from '../utils/apiCaller';
import { EXTENSION_CONFIGURATION_COMPONENT_RENDER_TYPES } from '../components/pages/Extensions/constants';
import clockSvg from '../assets/clock.svg';
const {
  getAPICallObject,
  getBiHeaders,
  getIRISAPICallObject,
} = requestConstructor;

let API_ENDPOINT = config.development.api_endpoint;
let API_AUTH_ENDPOINT = config.development.auth_endpoint;
let BI_API_ENDPOINT = config.development.bi_api_endpoint;
let CAMPAIGNS_API_ENDPOINT = config.development.campaigns_api_endpoint;
let LOYALTY_API_ENDPOINT = config.development.loyalty_api_endpoint;
let ORG_SETTINGS_API_ENDPOINT = config.development.org_settings_api_endpoint;
let INCENTIVES_API_ENDPOINT = config.development.incentives_api_endpoint;
let COUPON_API_ENDPOINT = config.development.coupon_api_endpoint;
let ADIONA_API_ENDPOINT = config.development.adiona_api_endpoint;
let EMF_API_ENDPOINT = config.development.emf_api_endpoint;
let INTOUCH_API_V3_ENDPOINT = config.development.intouch_api_v3_endpoint;
let NFS_ENDPOINT = config.development.nfs_endpoint;
const isProd = process.env.NODE_ENV === 'production';

if (isProd) {
  API_ENDPOINT = config.production.api_endpoint;
  API_AUTH_ENDPOINT = config.production.auth_endpoint;
  BI_API_ENDPOINT = config.production.bi_api_endpoint;
  CAMPAIGNS_API_ENDPOINT = config.production.campaigns_api_endpoint;
  LOYALTY_API_ENDPOINT = config.production.loyalty_api_endpoint;
  ORG_SETTINGS_API_ENDPOINT = config.production.org_settings_api_endpoint;
  INCENTIVES_API_ENDPOINT = config.production.incentives_api_endpoint;
  COUPON_API_ENDPOINT = config.production.coupon_api_endpoint;
  ADIONA_API_ENDPOINT = config.production.adiona_api_endpoint;
  EMF_API_ENDPOINT = config.production.emf_api_endpoint;
  INTOUCH_API_V3_ENDPOINT = config.production.intouch_api_v3_endpoint;
  NFS_ENDPOINT = config.production.nfs_endpoint;
}

function getAPICallObjectForAuth(
  method,
  body,
  isFileUpload = false,
  apiConfigs,
) {
  const token = loadItem('token');
  const orgID = loadItem('orgID');
  const user = loadItem('user');
  let headers;
  if (isFileUpload) {
    headers = {};
  } else {
    headers = {
      'Content-Type': 'application/json',
    };
  }

  if (user && user.refID) {
    headers['X-CAP-REMOTE-USER'] = user.refID;
  }

  if (process.env.NODE_ENV !== 'production' && orgID !== undefined) {
    headers['X-CAP-API-AUTH-ORG-ID'] = orgID;
  }

  if (process.env.NODE_ENV !== 'production' && token !== undefined) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (get(apiConfigs, 'headers')) {
    const configHeaders = apiConfigs.headers;
    headers = { ...headers, ...configHeaders };
  }

  const requestObj = {
    method,
    mode: 'cors',
    headers: new Headers(headers),
  };

  if (process.env.NODE_ENV === 'production') {
    requestObj.credentials = 'same-origin';
  }

  if (body && !isFileUpload) {
    requestObj.body = JSON.stringify(body);
  } else if (body && isFileUpload) {
    requestObj.body = body;
  }
  return requestObj;
}

function parseJSON(response) {
  return response.json();
}

function redirectIfUnauthenticated(response, skipRedirect = false) {
  const isUnauthorized = response.status === 401;
  const isLoginPage = window.location.pathname.indexOf('/login') !== -1;
  const isAuthUserCall =
    response.url.split('auth')[1] &&
    response.url.split('auth')[1].split('?')[0] === '/user';
  
  // Skip redirect if explicitly requested or if it's an auth user call on login page
  if (skipRedirect || (isLoginPage && isAuthUserCall)) {
    return;
  }
  
  if (isUnauthorized) {
    if (isProd) {
      const originUrl = window.location.origin;
      removeAuthenticationDetais();
      window.location = `${originUrl}${config.production.logout_url}`;
    } else {
      removeAuthenticationDetais();
      const loginPage = `${config.development.new_login_url}`;
      window.location = `${loginPage}`;
    }
  }
}

function checkStatus(response, skipRedirect = false) {
  if (response.status === 302 || response.redirected) {
    return response;
  }
  
  if (
    (response.status >= 200 && response.status < 300) ||
    response.status === 500 ||
    response.status === 400 ||
    response.status === 403 ||
    response.status === 404 ||  // Allow 404 for SSO domain check
    response.status === 409 ||
    response.status === 423 ||
    response.status === 429     //Allow 429 for rate limiting
  ) {
    return response;
  }

  // Auth-flow calls (skipRedirect=true) never trigger the global logout redirect, but a
  // bare 401 still fell through to the throw below, discarding the real JSON error body
  // (e.g. arya's "Login disallowed" message) before the saga ever saw it. Let it through
  // like the other error codes above so callers can read response.status/message themselves.
  if (response.status === 401 && skipRedirect) {
    return response;
  }

  redirectIfUnauthenticated(response, skipRedirect);

  const error = new Error(response.statusText);
  error.response = response;
  throw error;
}

function fetchWithTimeout(ms, promise) {
  //https://github.com/github/fetch/issues/175#issuecomment-125779262
  return new Promise(function(resolve, reject) {
    setTimeout(function() {
      reject({
        message: 'Request timeout',
        errorLocation: window.location.href,
      });
    }, ms);
    promise.then(resolve, reject);
  });
}
export function showError(errorMessage, url) {
  if (errorMessage.message) {
    CapNotification.error({
      key: 'error-notification',
      message: errorMessage.message,
    });
  }
}

export function showSuccess({ message }) {
  CapNotification.success({
    key: 'success-notification',
    message: message,
    duration: 5,
  });
}

export function showProcessNotification({ message }) {
  CapNotification.custom({
    key: 'progress-notification',
    message: message,
    duration: 5,
    icon: <img alt="progress-notification" src={clockSvg} />,
    bgColor: '#f87d23',
  });
}

function checkStatusCode(res) {
  if (res && res.code) {
    return /^[5][0-9][0-9]$/.test(res.code);
  }
  return false;
}
async function request(url, options, timeout = 50000, skipRedirect = false) {
  const fetchUrl =
    url.indexOf('?') !== -1
      ? `${url}&time=${Date.now()}`
      : `${url}?time=${Date.now()}`;

  try {
    const response = await fetchWithTimeout(timeout, fetch(fetchUrl, options));

    
    if (response.redirected) {
      return {
        success: true,
        redirected: true,
        status: response.status,
      };
    }

    checkStatus(response, skipRedirect);

    const contentType = response.headers.get('Content-Type');

    if (contentType && contentType.includes('text/html')) {
      const text = await response.text();
      return text;
    }

    const json = await parseJSON(response);

    if (!json) {
      console.warn('Received empty response.');
      return null;
    }

    return json;
  } catch (error) {
    // A deliberately cancelled request (AbortController — e.g. a superseded
    // typeahead keystroke) is not a failure; stay silent.
    if (error && error.name === 'AbortError') {
      return error;
    }
    if (error && error.message !== 'Unauthorized') {
      showError(error, url);
    }
    return error;
  }
}

// The rest of your code remains unchanged...

export const getLocizeMessage = async locale => {
  const appNameList = ['infinity_ui', 'loyalty_plus'];
  const response = await Promise.all(
    appNameList.map(appName => {
      const url = `${API_AUTH_ENDPOINT}/translations/${appName}/${locale}`;
      return request(url, getAPICallObject('GET'));
    }),
  );
  let data = {};
  response.forEach(item => {
    data = {
      ...data,
      ...item,
    };
  });
  return data;
};

export const logout = () => {
  const url = `${API_AUTH_ENDPOINT}/auth/logout`;
  return request(url, getAPICallObject('GET'));
};

export const logoutV2 = () => {
  const url = `${API_AUTH_ENDPOINT}/auth/v2/logout`;
  return request(url, getAPICallObject('GET'));
};

export const getOtpSession = () => {
  const url = `${API_AUTH_ENDPOINT}/auth/v2/otp-session`;
  return request(url, getAPICallObject('GET'), 10000, true);
};

export const changeProxyOrg = orgId => {
  const url = `${API_AUTH_ENDPOINT}/auth/setProxy/${orgId}`;
  return request(url, getAPICallObject('Post'));
};

export const getUserData = () => {
  const url = `${API_AUTH_ENDPOINT}/auth/user`;
  return request(url, getAPICallObject('GET'));
};

export const getLastSyncTime = () => {
  const url = `${BI_API_ENDPOINT}/data/lastsynctime`;
  return request(
    url,
    getAPICallObject('GET', null, false, { headers: getBiHeaders() }),
  );
};

export const getActiveBadgesCount = () => {
  const url = `${INCENTIVES_API_ENDPOINT}/badges?size=1&page=0&action=ACTIVATED&order=DESC&sortOn=LAST_UPDATED_DATE`;
  return request(url, getAPICallObject('GET'));
};

export const getOutboundCampaignsCount = () => {
  const url = `${CAMPAIGNS_API_ENDPOINT}/campaigns/filter`;
  return request(
    url,
    getIRISAPICallObject('POST', {
      campaignStates: ['LIVE'],
      limit: 1,
      offset: 0,
      messageStatsRequired: true,
    }),
  );
};

export const getActivePromotionsCountV1 = () => {
  const url = `${EMF_API_ENDPOINT}/programs/promotions/list`;
  return AxiosApi.get(url, { limit: 1, offset: 0, status: 'LIVE' }, { headers: { Accept: 'application/json', 'Content-Type': 'application/json' } })
    .then(res => {
      const total = res && res.data && res.data.pageDetails && res.data.pageDetails.totalEntries;
      return typeof total === 'number' ? total : 0;
    })
    .catch(() => 0);
};

export const getActivePromotionsCountV2 = () => {
  const url = `${INTOUCH_API_V3_ENDPOINT}/promotions?page=0&size=1&includeDraftDetails=true&status=LIVE&sortBy=lastModifiedOn&sortOrder=DESC`;
  return request(url, getAPICallObject('GET')).then(data => {
    const total = data && data.data && data.data.totalElements;
    return typeof total === 'number' ? total : 0;
  });
};

export const getActiveProgramsCount = () => {
  const url = `${LOYALTY_API_ENDPOINT}/programs`;
  return request(
    url,
    getAPICallObject('GET', null, false, {
      headers: { 'x-cap-compress-response': 'true' },
    }),
  ).then(data => {
    const list = data && data.result;
    return Array.isArray(list) ? list.length : 0;
  });
};

export const getActiveTiersCount = () => {
  const programsUrl = `${LOYALTY_API_ENDPOINT}/programs`;
  return request(
    programsUrl,
    getAPICallObject('GET', null, false, {
      headers: { 'x-cap-compress-response': 'true' },
    }),
  ).then(data => {
    const programs = data && data.result;
    if (!Array.isArray(programs) || programs.length === 0) return 0;
    const defaultProgram = programs.find(p => p.isDefault) || programs[0];
    const programId = defaultProgram && defaultProgram.id;
    if (!programId) return 0;
    const tiersUrl = `${LOYALTY_API_ENDPOINT}/strategy/tier/${programId}`;
    return request(tiersUrl, getAPICallObject('GET', null, false)).then(tiersData => {
      const slabs = tiersData && tiersData.result && tiersData.result.slabs;
      return Array.isArray(slabs) ? slabs.length : 0;
    });
  });
};

export const getMLPStatus = () => {
  const url = `${LOYALTY_API_ENDPOINT}/common/entityType/ALLOW_MLP`;
  return request(url, getAPICallObject('GET', null, false)).then(data => {
    const value = data && data.result && data.result['0'] && data.result['0'].value;
    return value === '1';
  });
};

export const getActiveJourneysCount = () => {
  const url = `${ADIONA_API_ENDPOINT}/journeys/list`;
  return request(
    url,
    getAPICallObject('POST', {
      limit: 10000,
      pageNumber: 0,
      statusList: ['LIVE', 'PAUSED', 'SUNSET'],
    }),
  ).then(data => {
    const list = data && data.result && data.result.data;
    return Array.isArray(list) ? list.length : 0;
  });
};

export const getActiveCouponSeriesCount = () => {
  const url = `${COUPON_API_ENDPOINT}/config/v2?limit=10000&offset=0&orderBy=LAST_MODIFIED_DATE&sort=DESC`;
  return request(url, getAPICallObject('GET')).then(data => {
    const { response = '' } = data || {};
    const decompressed = decompressJsonObject(response);
    return Array.isArray(decompressed) ? decompressed.length : 0;
  });
};

export const getLiveCampaignsCount = () => {
  const url = `${API_ENDPOINT}/quickLink/liveCounts`;
  return request(url, getAPICallObject('GET'));
};

export const getSMSCredits = () => {
  const orgID = loadItem('orgID');
  const url = `${API_AUTH_ENDPOINT}/org-settings/credits/credit-balance/${orgID}`;
  return request(url, getAPICallObject('GET'));
};

export const getChartDataByIdentifier = ({ chartIdentifier, requestData }) => {
  const url = `${BI_API_ENDPOINT}/data/reports/charts/identifier/${chartIdentifier}?type=NORMAL`;
  return request(
    url,
    getAPICallObjectForAuth('POST', requestData, false, {
      headers: getBiHeaders(),
    }),
    300000,
  );
};

export const getBeamerPosts = category => {
  const url = `${API_ENDPOINT}/beamer/${category}`;
  return request(url, getAPICallObject('GET'));
};

export const getPendingRequestCount = () => {
  const url = `${API_ENDPOINT}/quickLink/pendingRequstCount`;
  return request(url, getAPICallObject('GET'));
};

export const getPrograms = () => {
  const url = `${LOYALTY_API_ENDPOINT}/programs?includeSlabs=0&&includeCreatedInfo=0`;
  return request(url, getAPICallObject('GET'), 180000);
};

export const getBannerVisibilityStatus = () => {
  const url = `${API_ENDPOINT}/common/bannerVisibility`;
  return request(url, getAPICallObject('GET'));
};

export const getAllNudges = ({ programId }) => {
  const url = `${LOYALTY_API_ENDPOINT}/nudges/${programId}`;
  return request(url, getAPICallObject('GET'));
};

export const getIsOrgCreditEnabled = orgId => {
  const url = `${API_AUTH_ENDPOINT}/org-settings/credits/credit-enabled/${orgId}`;
  return request(url, getAPICallObject('GET'));
};

export const getIsTimelineCampaignDisabled = orgID => {
  const url = `${API_AUTH_ENDPOINT}/org/${orgID}/configkey?name=DISABLE_TIMELINE_CAMPAIGN&entity_id=${orgID}&scope=ORG`;
  return request(url, getAPICallObject('GET'));
};

export const getIsDvsCampaignDisabled = orgID => {
  const url = `${API_AUTH_ENDPOINT}/org/${orgID}/configkey?name=DISABLE_DVS_CAMPAIGN&entity_id=${orgID}&scope=ORG`;
  return request(url, getAPICallObject('GET'));
};

export const getExtensionList = ({ params }) => {
  const apiParams = getQueryParams(params);
  const url = `${ORG_SETTINGS_API_ENDPOINT}/avengers-metadata-v2/v2/extensions?${apiParams}`;
  return request(url, getAPICallObject('GET'));
};

export const getVerticalListForExtension = () => {
  const url = `${ORG_SETTINGS_API_ENDPOINT}/avengers-metadata-v2/v2/extensions/verticals`;
  return request(url, getAPICallObject('GET'));
};

export const createExtension = ({ body }) => {
  const url = `${ORG_SETTINGS_API_ENDPOINT}/avengers-metadata-v2/v2/extensions`;
  return request(url, getAPICallObject('POST', body));
};

export const getExtensionDetail = ({ params }) => {
  const url = `${ORG_SETTINGS_API_ENDPOINT}/avengers-metadata-v2/v2/extensions?packageName=${
    params.packageName
  }`;
  return request(url, getAPICallObject('GET'));
};

export const toggleActivateExtension = ({ body }) => {
  const url = `${ORG_SETTINGS_API_ENDPOINT}/avengers-metadata-v2/v2/extensions/org`;
  return request(url, getAPICallObject('POST', body));
};

export const addExtensionConfigOverides = ({ body, extensionId }) => {
  const url = `${ORG_SETTINGS_API_ENDPOINT}/avengers-metadata/${extensionId}/latest/config`;
  return request(url, getAPICallObject('POST', body));
};

export const getExtensionVersions = ({ params }) => {
  const url = `${ORG_SETTINGS_API_ENDPOINT}/avengers-metadata-v2/v2/extensions/fetchPublishedVersions?packageName=${
    params.packageName
  }&count=20&offset=0`;
  return request(url, getAPICallObject('GET'));
};

export const publishExtensionVersion = ({ body }) => {
  const url = `${ORG_SETTINGS_API_ENDPOINT}/avengers-metadata-v2/v2/extensions/publish`;
  return request(url, getAPICallObject('POST', body));
};

export const updateExtensionVersion = ({ body, extensionId }) => {
  const url = `${ORG_SETTINGS_API_ENDPOINT}/avengers-metadata/${extensionId}/version-update`;
  return request(url, getAPICallObject('POST', body));
};

export const processConfigurationApiByComponentType = ({
  body,
  componentType,
  extensionId,
  configurationName,
  params,
}) => {
  const apiParams = getQueryParams(params);
  const url = `${ORG_SETTINGS_API_ENDPOINT}/avengers-metadata/${extensionId}/processConfigurationApi/${configurationName}/${componentType}?${apiParams}`;
  switch (componentType) {
    case EXTENSION_CONFIGURATION_COMPONENT_RENDER_TYPES.UPLOAD_COMPONENT: {
      return request(url, getAPICallObject('POST', body, true));
    }
    case EXTENSION_CONFIGURATION_COMPONENT_RENDER_TYPES.DOWNLOAD_COMPONENT: {
      const headersData = {
        headers: extend({
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
        responseType: 'blob',
      };
      return AxiosApi.post(url, body, headersData);
    }
  }
};

let _navConfigCache = null;

export const getNavigationConfigApi = async () => {
  const url = `${API_AUTH_ENDPOINT}/mfe_navigations`;
  const response = await request(url, getAPICallObject('GET'));
  _navConfigCache = response;
  return response;
};

export const getCachedNavConfig = () => _navConfigCache;

export const getSupportedLocales = () => {
  const url = `${API_AUTH_ENDPOINT}/translations/supportedLocales`;
  return request(url, getAPICallObject('GET'));
};

export const loginV2 = ({ body }) => {
  const url = `${API_AUTH_ENDPOINT}/auth/v2/login`;
  return request(url, getAPICallObject('POST', body), 50000, true);
};

export const ssoLoginV2 = () => {
  const url = `${API_AUTH_ENDPOINT}/auth/v2/sso/login`;
  return request(url, getAPICallObject('GET'), 50000, true);
};

export const checkSsoDomain = ({ body }) => {
  const url = `${API_AUTH_ENDPOINT}/auth/v2/sso/check-domain`;
  return request(url, getAPICallObject('POST', body), 50000, true);
};

export const validateOTP = ({ body }) => {
  const url = `${API_AUTH_ENDPOINT}/auth/v2/validate-otp`;
  return request(url, getAPICallObject('POST', body), 50000, true);
};

export const resendOTP = () => {
  const url = `${API_AUTH_ENDPOINT}/auth/v2/resend-otp`;
  return request(url, getAPICallObject('GET'), 50000, true);
};

export const forgotPassword = ({ body }) => {
  const url = `${API_AUTH_ENDPOINT}/auth/v2/forgotPassword`;
  return request(url, getAPICallObject('POST', body), 50000, true);
};

export const setPassword = ({ body }) => {
  const url = `${API_AUTH_ENDPOINT}/auth/v2/password`;
  return request(url, getAPICallObject('POST', body), 50000, true);
};

export const getOrgHierarchy = () => {
  const url = `${ORG_SETTINGS_API_ENDPOINT}/avengers-metadata-v2/v2/organization/hierarchy`;
  return request(url, getAPICallObject('GET'));
};

export const getProductBrands = ({ q } = {}) => {
  const url = q
    ? `${API_ENDPOINT}/product/brands?q=${encodeURIComponent(q)}`
    : `${API_ENDPOINT}/product/brands`;
  return request(url, getAPICallObjectForAuth('GET'));
};

export const getProductBrandsList = ({ params } = {}) => {
  const apiParams = params ? getQueryParams(params) : '';
  const url = apiParams
    ? `${API_ENDPOINT}/product/brands?${apiParams}`
    : `${API_ENDPOINT}/product/brands`;
  return request(url, getAPICallObjectForAuth('GET'));
};

export const createProductBrands = ({ body }) => {
  const url = `${API_ENDPOINT}/product/brands`;
  return request(url, getAPICallObjectForAuth('POST', body));
};

export const updateProductBrands = ({ body }) => {
  const url = `${API_ENDPOINT}/product/brands`;
  return request(url, getAPICallObjectForAuth('PUT', body));
};

export const createProductSkus = ({ body }) => {
  const url = `${API_ENDPOINT}/product/skus`;
  return request(url, getAPICallObjectForAuth('POST', body));
};

// Collections (dimension groups) — proxied by the node layer to the NSE
// /v2/collections API (upstream renamed from /v2/groups).
export const getCollections = ({ params } = {}) => {
  const apiParams = params ? getQueryParams(params) : '';
  const url = apiParams ? `${API_ENDPOINT}/groups?${apiParams}` : `${API_ENDPOINT}/groups`;
  return request(url, getAPICallObjectForAuth('GET'));
};

export const getCollectionById = ({ id } = {}) => {
  const url = `${API_ENDPOINT}/groups/${encodeURIComponent(id)}`;
  return request(url, getAPICallObjectForAuth('GET'));
};

export const getCollectionAttributes = ({ params } = {}) => {
  const apiParams = params ? getQueryParams(params) : '';
  const url = apiParams
    ? `${API_ENDPOINT}/groups/attributes?${apiParams}`
    : `${API_ENDPOINT}/groups/attributes`;
  return request(url, getAPICallObjectForAuth('GET'));
};

export const createCollection = ({ body }) => {
  const url = `${API_ENDPOINT}/groups`;
  return request(url, getAPICallObjectForAuth('POST', body));
};

export const updateCollection = ({ id, body }) => {
  const url = `${API_ENDPOINT}/groups/${encodeURIComponent(id)}`;
  return request(url, getAPICallObjectForAuth('PUT', body));
};

export const getProductCategories = () => {
  const url = `${API_ENDPOINT}/product/categories`;
  return request(url, getAPICallObjectForAuth('GET'));
};

export const getProductCategoriesList = ({ params } = {}) => {
  const apiParams = params ? getQueryParams(params) : '';
  const url = apiParams
    ? `${API_ENDPOINT}/product/categories?${apiParams}`
    : `${API_ENDPOINT}/product/categories`;
  return request(url, getAPICallObjectForAuth('GET'));
};

export const createProductCategories = ({ body }) => {
  const url = `${API_ENDPOINT}/product/categories`;
  return request(url, getAPICallObjectForAuth('POST', body));
};

export const updateProductCategories = ({ body }) => {
  const url = `${API_ENDPOINT}/product/categories`;
  return request(url, getAPICallObjectForAuth('PUT', body));
};

export const getProductSkus = ({ params } = {}) => {
  const apiParams = params ? getQueryParams(params) : '';
  const url = apiParams
    ? `${API_ENDPOINT}/product/skus?${apiParams}`
    : `${API_ENDPOINT}/product/skus`;
  return request(url, getAPICallObjectForAuth('GET'));
};

export const getProductSkuByCode = ({ sku, params } = {}) => {
  const apiParams = params ? getQueryParams(params) : '';
  const url = apiParams
    ? `${API_ENDPOINT}/product/skus/${encodeURIComponent(sku)}?${apiParams}`
    : `${API_ENDPOINT}/product/skus/${encodeURIComponent(sku)}`;
  return request(url, getAPICallObjectForAuth('GET'));
};

export const updateProductSkus = ({ body }) => {
  const url = `${API_ENDPOINT}/product/skus`;
  return request(url, getAPICallObjectForAuth('PUT', body));
};

export const deleteProductSkuByCode = ({ sku, body } = {}) => {
  const url = `${API_ENDPOINT}/product/skus/${encodeURIComponent(sku)}`;
  return request(url, getAPICallObjectForAuth('DELETE', body));
};

export const getLocationsList = ({ params } = {}) => {
  const apiParams = params ? getQueryParams(params) : '';
  const url = apiParams
    ? `${API_ENDPOINT}/org/locations?${apiParams}`
    : `${API_ENDPOINT}/org/locations`;
  return request(url, getAPICallObjectForAuth('GET'));
};

export const getStoresList = ({ params } = {}) =>
  getLocationsList({ params: { ...params, type: 'STORE' } });

export const getZonesList = ({ params } = {}) => {
  const queryParams = getQueryParams(params);
  return request(
    `${API_ENDPOINT}/org/zones${queryParams ? `?${queryParams}` : ''}`,
    getAPICallObjectForAuth('GET'),
  );
};

export const getConceptsList = ({ params } = {}) =>
  getLocationsList({ params: { ...params, type: 'CONCEPT' } });

export const getZoneById = ({ id } = {}) =>
  request(`${API_ENDPOINT}/org/zones/${id}`, getAPICallObjectForAuth('GET'));

export const getZoneByCode = ({ code } = {}) =>
  request(
    `${API_ENDPOINT}/org/zones/by-code/${encodeURIComponent(code)}`,
    getAPICallObjectForAuth('GET'),
  );

export const createStores = ({ body }) =>
  request(
    `${API_ENDPOINT}/org/locations`,
    getAPICallObjectForAuth('POST', body),
  );

export const createZones = ({ body }) =>
  request(`${API_ENDPOINT}/org/zones`, getAPICallObjectForAuth('POST', body));

// Concepts & Tills (location entities). Listing/detail reuse the shared
// /org/locations endpoint via getLocationsList; creation goes through the
// dedicated BFF routes that proxy to v2/locations/concepts and
// v2/locations/tills (the concept hierarchy + till locale-inheritance handling
// lives on those upstream endpoints).
export const getLocations = ({ params } = {}) => getLocationsList({ params });

export const createConcepts = ({ concepts }) =>
  request(
    `${API_ENDPOINT}/locations/concepts`,
    getAPICallObjectForAuth('POST', { concepts }),
  );

export const updateConcepts = ({ concepts }) =>
  request(
    `${API_ENDPOINT}/locations/concepts`,
    getAPICallObjectForAuth('PUT', { concepts }),
  );

export const createTills = ({ tills }) =>
  request(
    `${API_ENDPOINT}/locations/tills`,
    getAPICallObjectForAuth('POST', { tills }),
  );

export const updateTills = ({ tills }) =>
  request(
    `${API_ENDPOINT}/locations/tills`,
    getAPICallObjectForAuth('PUT', { tills }),
  );

export const getCustomFields = ({ scope }) =>
  request(
    `${API_ENDPOINT}/org/custom-fields?scope=${encodeURIComponent(scope)}`,
    getAPICallObjectForAuth('GET'),
  );

// TODO: align with /org/locations once API PR lands. Every other store call
// (getStoresList, createStores) uses the /org/locations base; this PUT /stores
// route diverges and should be unified when the backend canonicalizes the path.
export const updateStore = ({ body }) =>
  request(`${API_ENDPOINT}/stores`, getAPICallObjectForAuth('PUT', body));

export const updateZone = ({ id, body }) =>
  request(
    `${API_ENDPOINT}/org/zones/${id}`,
    getAPICallObjectForAuth('PUT', body),
  );

export const getDisplayNames = () =>
  request(`${API_ENDPOINT}/org/display-names`, getAPICallObjectForAuth('GET'));

export const saveDisplayNames = ({ body }) =>
  request(
    `${API_ENDPOINT}/org/display-names`,
    getAPICallObjectForAuth('PUT', body),
  );

export const getProductAttributes = () =>
  request(`${API_ENDPOINT}/product/attributes`, getAPICallObjectForAuth('GET'));

export const getProductAttributeValues = ({ code }) =>
  request(
    `${API_ENDPOINT}/product/attributes/values?code=${encodeURIComponent(
      code,
    )}`,
    getAPICallObjectForAuth('GET'),
  );

export const getLabelAssignments = ({ entityType, entityIds }) => {
  const params = getQueryParams({ entityType, entityIds });
  return request(
    `${API_ENDPOINT}/labels/assignments?${params}`,
    getAPICallObjectForAuth('GET'),
  );
};

export const getLabels = ({ params }) => {
  const apiParams = getQueryParams(params);
  const url = `${API_ENDPOINT}/labels/list?${apiParams}`;
  return request(url, getAPICallObject('GET'));
};

export const createLabels = ({ labels }) => {
  const url = `${API_ENDPOINT}/labels`;
  return request(url, getAPICallObject('POST', { labels }));
};

export const updateLabels = ({ labels }) => {
  const url = `${API_ENDPOINT}/labels`;
  return request(url, getAPICallObject('PUT', { labels }));
};

export const assignLabels = ({ entityType, assignments }) => {
  const url = `${API_ENDPOINT}/labels/assignments`;
  return request(
    url,
    getAPICallObjectForAuth('POST', { entityType, assignments }),
  );
};

export const removeLabelAssignments = ({ entityType, removals }) => {
  const url = `${API_ENDPOINT}/labels/assignments`;
  return request(
    url,
    getAPICallObjectForAuth('DELETE', { entityType, removals }),
  );
};

export const getLabelExternalIdConfig = () => {
  const url = `${API_ENDPOINT}/labels/external-id-config`;
  return request(url, getAPICallObject('GET'));
};

export const getOrgUsers = () => {
  const url = `${API_AUTH_ENDPOINT}/auth/org/users`;
  return request(url, getAPICallObject('GET'));
};

export const getAttributes = ({ params }) => {
  const apiParams = getQueryParams(params);
  const url = `${API_ENDPOINT}/attributes/list?${apiParams}`;
  return request(url, getAPICallObject('GET'));
};

export const createAttributes = ({ attributes }) => {
  const url = `${API_ENDPOINT}/attributes`;
  return request(url, getAPICallObject('POST', { attributes }));
};

export const updateAttributes = ({ attributes }) => {
  const url = `${API_ENDPOINT}/attributes`;
  return request(url, getAPICallObject('PUT', { attributes }));
};

// aira (Ask aiRA) API
// AIRA_HOST: dev → full https host (same cluster as other endpoints), prod → '' (same-origin)
const AIRA_HOST = API_AUTH_ENDPOINT.replace('/arya/api/v1', '');

export const createAiraSession = firstMessage => {
  const url = `${AIRA_HOST}/ask-aira/copilot/session/create`;
  return request(
    url,
    getAPICallObject('POST', { first_message: firstMessage }),
  );
};

export const getAiraSession = sessionId => {
  const url = `${AIRA_HOST}/ask-aira/copilot/session/${sessionId}`;
  return request(url, getAPICallObject('GET'));
};

export const transcribeAiraAudio = formData => {
  const url = `${AIRA_HOST}/ask-aira/media/transcribe`;
  return request(url, getAPICallObject('POST', formData, true));
};

export const uploadAiraFiles = (sessionId, files) => {
  const formData = new FormData();
  files.forEach(file => formData.append('files', file));
  const url = `${AIRA_HOST}/ask-aira/copilot/chat/upload_files?session_id=${encodeURIComponent(
    sessionId,
  )}`;
  return request(url, getAPICallObject('POST', formData, true));
};

export const getAiraRecentSessions = () => {
  const url = `${AIRA_HOST}/ask-aira/copilot/session/list?offset=0&limit=4`;
  return request(url, getAPICallObject('GET'));
};

// Distinct values for a dimension attribute (rule-builder value suggestions) —
// served by the NFS meta API.
export const getDimensionAttrValues = ({ dimName, attr }) => {
  const url = `${NFS_ENDPOINT}/meta/getdimensionattrvalues/${encodeURIComponent(
    dimName,
  )}/${encodeURIComponent(attr)}`;
  return request(url, getAPICallObject('GET'));
};

// On-demand membership re-evaluation (no request body). Upstream answers
// 400 (not found / DEACTIVATED), 409 (already in progress) or 503.
export const refreshCollection = ({ id }) => {
  const url = `${API_ENDPOINT}/groups/${encodeURIComponent(id)}/refresh`;
  return request(url, getAPICallObjectForAuth('POST'));
};

// Typeahead search over a high-cardinality dimension attribute's values —
// same NFS meta API and arya {status, response} envelope as
// getDimensionAttrValues (low-cardinality attributes preload the full list
// with that call instead). Mirrors cap-filter-ui's typeahead endpoint.
export const searchDimensionAttrValues = ({
  dimName,
  attr,
  searchText,
  isHighCardinality = true,
  // Optional AbortController signal — lets the caller cancel a superseded
  // typeahead request instead of letting it run to completion.
  signal,
}) => {
  const url = `${NFS_ENDPOINT}/meta/searchdimensionattrvalues/${encodeURIComponent(
    dimName,
  )}/${encodeURIComponent(attr)}/${encodeURIComponent(
    searchText,
  )}/${isHighCardinality}`;
  const options = getAPICallObject('GET');
  if (signal) options.signal = signal;
  return request(url, options);
};
